import i18n from "@/i18n";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function getLocale(): string {
  const lang = i18n.language || "en";
  const localeMap: Record<string, string> = { en: "en-US", es: "es-419" };
  return localeMap[lang] || lang;
}

const VALID_CURRENCY_RE = /^[A-Z]{3}$/;

// AED intentionally omitted: it renders as the textual "AED" symbol that
// Intl emits by default (OCT-355 — the bundled dirham glyph font was a
// defective placeholder). SAR keeps its dedicated symbol.
const CUSTOM_CURRENCY_SYMBOLS: Record<string, string> = { SAR: "﷼" };

export function formatCurrency(amount: number, currency: string | null | undefined = "USD"): string {
  const code = (currency && VALID_CURRENCY_RE.test(currency)) ? currency : "USD";
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  try {
    const formatted = new Intl.NumberFormat(getLocale(), { style: "currency", currency: code }).format(safeAmount);
    const customSymbol = CUSTOM_CURRENCY_SYMBOLS[code];
    if (customSymbol) {
      return formatted.replace(code, customSymbol);
    }
    return formatted;
  } catch {
    return new Intl.NumberFormat(getLocale(), { style: "currency", currency: "USD" }).format(safeAmount);
  }
}

export function formatCompactCurrency(amount: number, currency: string | null | undefined = "USD"): string {
  const code = (currency && VALID_CURRENCY_RE.test(currency)) ? currency : "USD";
  try {
    const formatted = new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency: code,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(amount);
    const customSymbol = CUSTOM_CURRENCY_SYMBOLS[code];
    if (customSymbol) {
      return formatted.replace(code, customSymbol);
    }
    return formatted;
  } catch {
    return new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(amount);
  }
}

/**
 * A number abbreviated for a chart axis - `220000` becomes `220K`.
 *
 * Deliberately carries no currency symbol, unlike
 * {@link formatCompactCurrency}: a 180px-tall chart's y-axis has room for a
 * tick label of about four characters, and the card header plus the tooltip
 * already say which currency the bars are in.
 *
 * OCT-11477: the dashboard's money charts used to buy that width back with a
 * negative `margin.left`, which clipped the labels instead of narrowing them -
 * a `220000` tick rendered as `20000`, understating the aging balance by an
 * order of magnitude.
 */
export function formatCompactNumber(value: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(getLocale(), {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(safe);
  } catch {
    return String(safe);
  }
}

function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

export function normalizeDate(raw: string): string {
  if (!raw || typeof raw !== "string") return raw;
  const s = raw.trim();
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(s)) {
    const parts = s.split(/[-/]/);
    const y = parts[0] ?? "";
    const m = parts[1] ?? "";
    const d = parts[2] ?? "";
    const yn = parseInt(y, 10), mn = parseInt(m, 10), dn = parseInt(d, 10);
    if (!isValidCalendarDate(yn, mn, dn)) return s;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(s)) {
    const parts = s.split(/[-/]/);
    const a = parts[0] ?? "";
    const b = parts[1] ?? "";
    const y = parts[2] ?? "";
    const aNum = parseInt(a, 10);
    const bNum = parseInt(b, 10);
    const yn = parseInt(y, 10);
    let day: number, month: number;
    if (aNum > 12) {
      day = aNum;
      month = bNum;
    } else if (bNum > 12) {
      month = aNum;
      day = bNum;
    } else {
      day = aNum;
      month = bNum;
    }
    if (!isValidCalendarDate(yn, month, day)) return s;
    return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return s;
}

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  let d: Date;
  if (typeof date === "string") {
    const normalized = normalizeDate(date);
    d = new Date(normalized);
    if (isNaN(d.getTime())) {
      d = new Date(date);
    }
  } else {
    d = date;
  }
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString(getLocale(), options || { month: "2-digit", day: "2-digit", year: "numeric" });
}

export function formatNumber(amount: number, options?: Intl.NumberFormatOptions): string {
  return amount.toLocaleString(getLocale(), options);
}

export function parseLocaleNumber(value: string): number {
  if (!value || typeof value !== "string") return 0;
  let s = value.trim();
  let negative = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    negative = true;
    s = s.slice(1, -1).trim();
  }
  if (s.startsWith("-")) {
    negative = true;
    s = s.slice(1).trim();
  }
  s = s.replace(/[^0-9.,]/g, "");
  if (!s) return 0;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;
  let result: number;
  if (lastComma > lastDot) {
    result = parseFloat(s.replace(/\./g, "").replace(",", "."));
  } else if (lastDot > lastComma) {
    if (dotCount > 1) {
      result = parseFloat(s.replace(/\./g, ""));
    } else {
      const afterDot = s.slice(lastDot + 1);
      const beforeDot = s.slice(0, lastDot);
      if (
        commaCount === 0 && afterDot.length === 3 && beforeDot.length >= 2
        && /^\d{1,3}\.\d{3}$/.test(s.replace(/,/g, ""))
      ) {
        result = parseFloat(s.replace(/\./g, ""));
      } else {
        result = parseFloat(s.replace(/,/g, ""));
      }
    }
  } else if (lastComma >= 0 && lastDot < 0) {
    const parts = s.split(",");
    if (parts.length === 2 && (parts[1]?.length ?? 0) <= 2) {
      result = parseFloat(s.replace(",", "."));
    } else {
      result = parseFloat(s.replace(/,/g, ""));
    }
  } else {
    result = parseFloat(s);
  }
  if (isNaN(result)) return 0;
  return negative ? -result : result;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {}
    try {
      const blob = new Blob([text], { type: "text/plain" });
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
      return true;
    } catch {}
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (ok) return true;
  } catch {
    document.body.removeChild(textarea);
  }
  if (navigator.share) {
    try {
      await navigator.share({ text });
      return true;
    } catch {}
  }
  return false;
}
